import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Input,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { SnAbilityService } from '@lib/ability';
import { SnCameraGrid, SnWidget } from '@lib/api/entities';
import { SnCameraGridsAPI, SnOperationalViewsAPI } from '@lib/api/services';
import { SnCameraGridActionsService } from '@lib/cameras';
import { snZoomIn, snZoomOut } from '@lib/common/animations';

import { CameraGridsDialogComponent } from '../../components';

@Component({
  selector: 'sn-camera-grids-widget',
  templateUrl: './camera-grids-widget.component.html',
  styleUrls: ['./camera-grids-widget.component.scss'],
  providers: [SnCameraGridActionsService],
  animations: [snZoomIn, snZoomOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraGridsWidgetComponent implements OnInit, OnDestroy {
  @Input() public widget: SnWidget;
  @Input() public viewId: string;

  public cameraGrid: SnCameraGrid;
  public changeTitle = false;
  public readonly canChange: boolean;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _dialog: MatDialog,
    private readonly _operationalViewsAPI: SnOperationalViewsAPI,
    private readonly _cd: ChangeDetectorRef,
    private readonly _router: Router,
    private readonly _ability: SnAbilityService,
    private readonly _cameraGridsAPI: SnCameraGridsAPI,
  ) {
    this.canChange = this._ability.can({ subject: 'dashboards/widgetobject', action: 'change' });
  }

  public ngOnInit(): void {
    this._checkForGrid();
  }

  public ngOnDestroy(): void {
    this._router.navigate([], { queryParams: {} });
    this._destroy$.next();
    this._destroy$.complete();
  }

  public openCameraGridDialog(): void {
    const dialogRef = this._dialog.open(CameraGridsDialogComponent);
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        this._cd.detectChanges();
        if (result) {
          this._getGrid(result.id);
          this._cd.detectChanges();
        }
      });
  }

  public back(): void {
    this.cameraGrid = null;
    this._cd.detectChanges();
  }

  public editTitle(): void {
    this.changeTitle = true;
  }

  public setTitle(value: boolean): void {
    this.changeTitle = value;
    this._cd.detectChanges();
  }

  public setUpdateTitle(value: string): void {
    this.widget.name = value;
    this._cd.detectChanges();
  }

  private _checkForGrid(): void {
    if (!this.widget.widgetObjects.length) {
      return;
    }

    if (this.widget.widgetObjects[0].object) {
      this.cameraGrid = new SnCameraGrid(this.widget.widgetObjects[0].object);
      this._cd.detectChanges();
    }
  }

  private _saveWidgetGrid(grid: SnCameraGrid): void {
    this._operationalViewsAPI
      .updateViewWidget(this.viewId, this.widget.id, {
        widgetObjects: [
          {
            contentType: 'cameraview',
            objectId: grid.id,
          },
        ],
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((widget) => {
        this.widget.widgetObjects = widget.widgetObjects;
        this.cameraGrid = new SnCameraGrid(this.widget.widgetObjects[0].object);
        this._cd.detectChanges();
      });
  }

  // Needed to get grid again or cameras wont load
  private _getGrid(id: string): void {
    this._cameraGridsAPI
      .getCameraGrid(id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((grid) => {
        this.cameraGrid = grid;
        this._cd.detectChanges();
        this._saveWidgetGrid(this.cameraGrid);
      });
  }
}
